/**
 * ImageVector Exporter
 * Generates Kotlin Compose Multiplatform ImageVector code from layers
 */

import type { Layer, TextLayer, ShapeLayer, ImageLayer } from '@/types/layers';
import type { AdFormat } from '@/types/formats';
import type { CanvasBackground } from '@/types/template';
import type { Brush } from '@/types/brush';
import { resolvePosition, type ContainerDimensions } from '@/lib/position/resolver';

// Extract hex color from Brush (for ImageVector, gradients fall back to first stop)
function brushToHex(brush: Brush | undefined): string | null {
  if (!brush || brush.type === 'transparent') {
    return null;
  }
  if (brush.type === 'solid') {
    return brush.color.replace('#', '');
  }
  if (brush.type === 'linear-gradient' || brush.type === 'radial-gradient' || brush.type === 'sweep-gradient') {
    return brush.stops[0]?.color.replace('#', '') || null;
  }
  return null;
}

interface ExportContext {
  format: AdFormat;
  variableValues: Record<string, string>;
  indent: number;
}

/**
 * Export layers to Kotlin ImageVector code
 */
export function exportToImageVector(
  layers: Layer[],
  format: AdFormat,
  background: CanvasBackground,
  variableValues: Record<string, string> = {},
  name: string = 'GeneratedIcon'
): string {
  const ctx: ExportContext = {
    format,
    variableValues,
    indent: 0,
  };

  const container: ContainerDimensions = {
    width: format.width,
    height: format.height,
  };

  // Generate path code for each layer
  const pathCodes: string[] = [];

  // Background
  const bgCode = generateBackgroundPath(background, format, ctx);
  if (bgCode) pathCodes.push(bgCode);

  // Layers
  for (const layer of layers.filter(l => l.visible)) {
    const code = generateLayerPath(layer, container, ctx);
    if (code) pathCodes.push(code);
  }

  const className = toPascalCase(name);

  return `package com.example.icons

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathFillType
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.path
import androidx.compose.ui.unit.dp

/**
 * Generated ImageVector from AdStudio
 * Original size: ${format.width}x${format.height}
 */
val ${className}: ImageVector
    get() {
        if (_${className} != null) {
            return _${className}!!
        }
        _${className} = ImageVector.Builder(
            name = "${className}",
            defaultWidth = ${format.width}.dp,
            defaultHeight = ${format.height}.dp,
            viewportWidth = ${format.width}f,
            viewportHeight = ${format.height}f
        ).apply {
${pathCodes.map(c => indent(c, 12)).join('\n')}
        }.build()
        return _${className}!!
    }

private var _${className}: ImageVector? = null
`;
}

function generateBackgroundPath(bg: CanvasBackground, format: AdFormat, ctx: ExportContext): string | null {
  if (bg.type === 'image') {
    return `// Image background not supported in ImageVector`;
  }

  if (bg.type === 'transparent') {
    return null; // No background
  }

  const color = brushToHex(bg as Brush);
  if (!color) return null;

  const isGradient = bg.type === 'linear-gradient' || bg.type === 'radial-gradient';
  const comment = isGradient ? '// Background gradient - using first color\n' : '';

  return `${comment}path(fill = SolidColor(Color(0xFF${color}))) {
    moveTo(0f, 0f)
    lineTo(${format.width}f, 0f)
    lineTo(${format.width}f, ${format.height}f)
    lineTo(0f, ${format.height}f)
    close()
}`;
}

function generateLayerPath(layer: Layer, container: ContainerDimensions, ctx: ExportContext): string | null {
  const layerWidth = typeof layer.width === 'number' ? layer.width : 100;
  const layerHeight = typeof layer.height === 'number' ? layer.height : 100;
  const layerDims = { width: layerWidth, height: layerHeight };
  const pos = resolvePosition(layer.position, container, layerDims);

  switch (layer.type) {
    case 'shape':
      return generateShapePath(layer as ShapeLayer, pos);
    case 'text':
      // Text requires font rendering - add as comment
      return `// Text layer: "${(layer as TextLayer).content.text}" at (${pos.x}, ${pos.y})
// Note: Text requires font rendering and is not included in vector paths`;
    case 'image':
      // Images can't be represented in ImageVector
      return `// Image layer at (${pos.x}, ${pos.y})
// Note: Images cannot be embedded in ImageVector`;
    default:
      return `// Unsupported layer type: ${layer.type}`;
  }
}

function generateShapePath(layer: ShapeLayer, pos: { x: number; y: number }): string {
  const { content } = layer;
  const width = typeof layer.width === 'number' ? layer.width : 100;
  const height = typeof layer.height === 'number' ? layer.height : 100;

  const fillHex = brushToHex(content.fill);
  const fillColor = fillHex ? `SolidColor(Color(0xFF${fillHex}))` : 'null';

  const strokeHex = brushToHex(content.stroke);
  const strokeColor = strokeHex ? `SolidColor(Color(0xFF${strokeHex}))` : 'null';

  const strokeWidth = content.strokeWidth || 0;

  const pathParams = [
    `fill = ${fillColor}`,
    strokeColor !== 'null' ? `stroke = ${strokeColor}` : null,
    strokeWidth > 0 ? `strokeLineWidth = ${strokeWidth}f` : null,
  ].filter(Boolean).join(',\n    ');

  switch (content.shapeType) {
    case 'rectangle': {
      const rx = content.borderRadius || 0;
      if (rx > 0) {
        // Rounded rectangle
        return `path(
    ${pathParams}
) {
    // Rounded rectangle at (${pos.x}, ${pos.y}) size ${width}x${height} radius ${rx}
    moveTo(${pos.x + rx}f, ${pos.y}f)
    lineTo(${pos.x + width - rx}f, ${pos.y}f)
    arcTo(${pos.x + width - rx}f, ${pos.y}f, ${pos.x + width}f, ${pos.y + rx}f, -90f, 90f, false)
    lineTo(${pos.x + width}f, ${pos.y + height - rx}f)
    arcTo(${pos.x + width - rx}f, ${pos.y + height - rx}f, ${pos.x + width}f, ${pos.y + height}f, 0f, 90f, false)
    lineTo(${pos.x + rx}f, ${pos.y + height}f)
    arcTo(${pos.x}f, ${pos.y + height - rx}f, ${pos.x + rx}f, ${pos.y + height}f, 90f, 90f, false)
    lineTo(${pos.x}f, ${pos.y + rx}f)
    arcTo(${pos.x}f, ${pos.y}f, ${pos.x + rx}f, ${pos.y + rx}f, 180f, 90f, false)
    close()
}`;
      }
      return `path(
    ${pathParams}
) {
    // Rectangle at (${pos.x}, ${pos.y}) size ${width}x${height}
    moveTo(${pos.x}f, ${pos.y}f)
    lineTo(${pos.x + width}f, ${pos.y}f)
    lineTo(${pos.x + width}f, ${pos.y + height}f)
    lineTo(${pos.x}f, ${pos.y + height}f)
    close()
}`;
    }

    case 'circle':
    case 'ellipse': {
      const rx = width / 2;
      const ry = height / 2;
      const cx = pos.x + rx;
      const cy = pos.y + ry;

      return `path(
    ${pathParams}
) {
    // ${content.shapeType === 'circle' ? 'Circle' : 'Ellipse'} at center (${cx}, ${cy}) radius ${rx}x${ry}
    moveTo(${cx + rx}f, ${cy}f)
    arcTo(${cx - rx}f, ${cy - ry}f, ${cx + rx}f, ${cy + ry}f, 0f, 180f, false)
    arcTo(${cx - rx}f, ${cy - ry}f, ${cx + rx}f, ${cy + ry}f, 180f, 180f, false)
    close()
}`;
    }

    case 'line': {
      const x2 = pos.x + width;
      const y2 = pos.y + height;
      return `path(
    stroke = ${strokeColor !== 'null' ? strokeColor : 'SolidColor(Color.Black)'},
    strokeLineWidth = ${strokeWidth || 1}f
) {
    // Line from (${pos.x}, ${pos.y}) to (${x2}, ${y2})
    moveTo(${pos.x}f, ${pos.y}f)
    lineTo(${x2}f, ${y2}f)
}`;
    }

    default:
      return `// Unsupported shape type: ${content.shapeType}`;
  }
}

function toPascalCase(str: string): string {
  return str
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, c) => c.toUpperCase())
    .replace(/^./, c => c.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, '');
}

function indent(str: string, spaces: number): string {
  const prefix = ' '.repeat(spaces);
  return str.split('\n').map(line => prefix + line).join('\n');
}

/**
 * Download ImageVector as Kotlin file
 */
export function downloadImageVector(code: string, filename: string): void {
  const blob = new Blob([code], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.kt') ? filename : `${filename}.kt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
